'use client'

import { use, useState, useMemo } from 'react'
import Link from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate } from '@/components/app'
import { MEDIUMS, type MediumId, type PeriodId, getMovementsByPeriod } from '@/lib/ontology'

// =============================================================================
// DATA
// =============================================================================

const categoryData: Record<string, {
  name: string
  dateRange: string
  volumes: Record<'all' | MediumId, string>
  lots: Record<'all' | MediumId, string>
  changes: Record<'all' | MediumId, number>
  avgHammer: string
  description: string
}> = {
  'contemporary': {
    name: 'Contemporary',
    dateRange: '1970+',
    volumes: { all: '$4.2B', paintings: '$2.8B', sculpture: '$680M', prints: '$420M', photography: '$180M', drawings: '$95M', 'mixed-media': '$25M' },
    lots: { all: '124K', paintings: '48K', sculpture: '18K', prints: '32K', photography: '14K', drawings: '10K', 'mixed-media': '2K' },
    changes: { all: 12.4, paintings: 14.2, sculpture: 8.8, prints: 18.6, photography: 6.2, drawings: 9.5, 'mixed-media': 22.1 },
    avgHammer: '$34K',
    description: 'Works created from 1970 to present, including Neo-Expressionism, Street Art, and Digital Art.',
  },
  'post-war': {
    name: 'Post-War',
    dateRange: '1945-1970',
    volumes: { all: '$2.1B', paintings: '$1.4B', sculpture: '$380M', prints: '$180M', photography: '$85M', drawings: '$48M', 'mixed-media': '$7M' },
    lots: { all: '67K', paintings: '28K', sculpture: '12K', prints: '16K', photography: '6K', drawings: '4K', 'mixed-media': '1K' },
    changes: { all: 8.2, paintings: 9.5, sculpture: 6.4, prints: 12.1, photography: 4.8, drawings: 5.2, 'mixed-media': 8.9 },
    avgHammer: '$31K',
    description: 'Art created 1945-1970, including Abstract Expressionism, Pop Art, and Minimalism.',
  },
  'modern': {
    name: 'Modern',
    dateRange: '1880-1945',
    volumes: { all: '$3.8B', paintings: '$2.6B', sculpture: '$520M', prints: '$380M', photography: '$120M', drawings: '$165M', 'mixed-media': '$15M' },
    lots: { all: '89K', paintings: '42K', sculpture: '14K', prints: '18K', photography: '8K', drawings: '6K', 'mixed-media': '1K' },
    changes: { all: 5.1, paintings: 6.2, sculpture: 3.8, prints: 8.4, photography: 2.1, drawings: 4.5, 'mixed-media': 5.8 },
    avgHammer: '$43K',
    description: 'European art 1880-1945, including Impressionism, Cubism, and Surrealism.',
  },
  '19th-century': {
    name: '19th Century',
    dateRange: '1800-1880',
    volumes: { all: '$1.2B', paintings: '$920M', sculpture: '$145M', prints: '$65M', photography: '$28M', drawings: '$38M', 'mixed-media': '$4M' },
    lots: { all: '28K', paintings: '16K', sculpture: '4K', prints: '4K', photography: '2K', drawings: '1.5K', 'mixed-media': '0.5K' },
    changes: { all: 3.4, paintings: 4.1, sculpture: 2.2, prints: 5.8, photography: 1.5, drawings: 2.8, 'mixed-media': 3.2 },
    avgHammer: '$43K',
    description: 'Art from 1800-1880, including Neoclassicism, Romanticism, and Realism.',
  },
  'old-masters': {
    name: 'Old Masters',
    dateRange: 'pre-1800',
    volumes: { all: '$1.4B', paintings: '$1.1B', sculpture: '$180M', prints: '$45M', photography: '$0', drawings: '$72M', 'mixed-media': '$3M' },
    lots: { all: '34K', paintings: '18K', sculpture: '6K', prints: '5K', photography: '0', drawings: '4K', 'mixed-media': '1K' },
    changes: { all: -2.1, paintings: -1.8, sculpture: -3.2, prints: 1.2, photography: 0, drawings: -0.8, 'mixed-media': -4.5 },
    avgHammer: '$41K',
    description: 'European paintings and drawings before 1800, from Renaissance to Baroque.',
  },
}

// Top artists by era with colors for initials
const topArtistsByEra: Record<string, { name: string; slug: string; initials: string; color: string; volume: string; change: number; tier: string; medium: MediumId }[]> = {
  'contemporary': [
    { name: 'Jean-Michel Basquiat', slug: 'jean-michel-basquiat', initials: 'JB', color: '#E63946', volume: '$285M', change: 12.1, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Yayoi Kusama', slug: 'yayoi-kusama', initials: 'YK', color: '#E85D75', volume: '$180M', change: 18.4, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Banksy', slug: 'banksy', initials: 'B', color: '#2A2A2A', volume: '$98M', change: 8.5, tier: 'Blue-chip', medium: 'prints' },
    { name: 'Kaws', slug: 'kaws', initials: 'KW', color: '#FF69B4', volume: '$72M', change: -8.4, tier: 'Blue-chip', medium: 'sculpture' },
    { name: 'Amoako Boafo', slug: 'amoako-boafo', initials: 'AB', color: '#4ECDC4', volume: '$45M', change: 42.1, tier: 'Mid-market', medium: 'paintings' },
  ],
  'post-war': [
    { name: 'Gerhard Richter', slug: 'gerhard-richter', initials: 'GR', color: '#7B68EE', volume: '$156M', change: -2.3, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Andy Warhol', slug: 'andy-warhol', initials: 'AW', color: '#FF4500', volume: '$142M', change: 6.8, tier: 'Blue-chip', medium: 'prints' },
    { name: 'Mark Rothko', slug: 'mark-rothko', initials: 'MR', color: '#8B0000', volume: '$98M', change: 8.4, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Roy Lichtenstein', slug: 'roy-lichtenstein', initials: 'RL', color: '#FFD700', volume: '$86M', change: 4.5, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Alexander Calder', slug: 'alexander-calder', initials: 'AC', color: '#FF6347', volume: '$62M', change: 5.2, tier: 'Blue-chip', medium: 'sculpture' },
  ],
  'modern': [
    { name: 'Claude Monet', slug: 'claude-monet', initials: 'CM', color: '#9B7EBD', volume: '$320M', change: 5.2, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Pablo Picasso', slug: 'pablo-picasso', initials: 'PP', color: '#4A90D9', volume: '$285M', change: 3.8, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Henri Matisse', slug: 'henri-matisse', initials: 'HM', color: '#FF6B6B', volume: '$145M', change: 6.2, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Fernand Léger', slug: 'fernand-leger', initials: 'FL', color: '#2ECC71', volume: '$78M', change: 4.1, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Alberto Giacometti', slug: 'alberto-giacometti', initials: 'AG', color: '#95A5A6', volume: '$68M', change: 2.8, tier: 'Blue-chip', medium: 'sculpture' },
  ],
  '19th-century': [
    { name: 'Vincent van Gogh', slug: 'vincent-van-gogh', initials: 'VG', color: '#F4A460', volume: '$180M', change: 4.2, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Edgar Degas', slug: 'edgar-degas', initials: 'ED', color: '#DEB887', volume: '$145M', change: 4.1, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Pierre-Auguste Renoir', slug: 'pierre-auguste-renoir', initials: 'PR', color: '#F08080', volume: '$120M', change: 2.8, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Camille Pissarro', slug: 'camille-pissarro', initials: 'CP', color: '#90EE90', volume: '$68M', change: 3.2, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Auguste Rodin', slug: 'auguste-rodin', initials: 'AR', color: '#708090', volume: '$52M', change: 1.8, tier: 'Blue-chip', medium: 'sculpture' },
  ],
  'old-masters': [
    { name: 'Rembrandt', slug: 'rembrandt', initials: 'R', color: '#8B4513', volume: '$98M', change: -1.2, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Peter Paul Rubens', slug: 'peter-paul-rubens', initials: 'PR', color: '#CD853F', volume: '$72M', change: -2.4, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Canaletto', slug: 'canaletto', initials: 'C', color: '#87CEEB', volume: '$45M', change: 1.5, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'Giovanni Battista Tiepolo', slug: 'giovanni-tiepolo', initials: 'GT', color: '#DDA0DD', volume: '$32M', change: -0.8, tier: 'Blue-chip', medium: 'paintings' },
    { name: 'François Boucher', slug: 'francois-boucher', initials: 'FB', color: '#FFB6C1', volume: '$28M', change: 2.1, tier: 'Blue-chip', medium: 'paintings' },
  ],
}

// Recent sales by era
const recentSalesByEra: Record<string, { id: string; artist: string; artistSlug: string; work: string; house: string; hammer: string; premium: string; medium: MediumId; date: string }[]> = {
  'contemporary': [
    { id: 'c1', artist: 'Jean-Michel Basquiat', artistSlug: 'jean-michel-basquiat', work: 'Untitled (Devil)', house: "Christie's", hammer: '$42.4M', premium: '+6%', medium: 'paintings', date: '5 days ago' },
    { id: 'c2', artist: 'Jeff Koons', artistSlug: 'jeff-koons', work: 'Balloon Dog (Orange)', house: "Sotheby's", hammer: '$24.8M', premium: '+24%', medium: 'sculpture', date: '1 week ago' },
    { id: 'c3', artist: 'Banksy', artistSlug: 'banksy', work: 'Girl with Balloon', house: "Sotheby's", hammer: '$12.1M', premium: '+18%', medium: 'prints', date: '2 weeks ago' },
    { id: 'c4', artist: 'Cindy Sherman', artistSlug: 'cindy-sherman', work: 'Untitled Film Still #21', house: 'Phillips', hammer: '$3.2M', premium: '+28%', medium: 'photography', date: '2 weeks ago' },
  ],
  'post-war': [
    { id: 'pw1', artist: 'Mark Rothko', artistSlug: 'mark-rothko', work: 'No. 7', house: "Sotheby's", hammer: '$18.2M', premium: '+21%', medium: 'paintings', date: '2 weeks ago' },
    { id: 'pw2', artist: 'Andy Warhol', artistSlug: 'andy-warhol', work: 'Shot Sage Blue Marilyn', house: "Christie's", hammer: '$28.5M', premium: '+14%', medium: 'prints', date: '1 week ago' },
    { id: 'pw3', artist: 'Alexander Calder', artistSlug: 'alexander-calder', work: 'Untitled Mobile', house: 'Phillips', hammer: '$8.4M', premium: '+12%', medium: 'sculpture', date: '3 weeks ago' },
    { id: 'pw4', artist: 'Jasper Johns', artistSlug: 'jasper-johns', work: 'Flag', house: "Christie's", hammer: '$15.2M', premium: '+8%', medium: 'paintings', date: '1 month ago' },
  ],
  'modern': [
    { id: 'm1', artist: 'Claude Monet', artistSlug: 'claude-monet', work: 'Nymphéas', house: "Christie's", hammer: '$74.0M', premium: '+12%', medium: 'paintings', date: '3 days ago' },
    { id: 'm2', artist: 'Pablo Picasso', artistSlug: 'pablo-picasso', work: 'Femme assise', house: "Sotheby's", hammer: '$42.8M', premium: '+7%', medium: 'paintings', date: '5 days ago' },
    { id: 'm3', artist: 'Alberto Giacometti', artistSlug: 'alberto-giacometti', work: 'Walking Man I', house: "Sotheby's", hammer: '$28.4M', premium: '+15%', medium: 'sculpture', date: '2 weeks ago' },
    { id: 'm4', artist: 'Henri Matisse', artistSlug: 'henri-matisse', work: 'La Danse', house: "Christie's", hammer: '$18.6M', premium: '+11%', medium: 'paintings', date: '3 weeks ago' },
  ],
  '19th-century': [
    { id: '19c1', artist: 'Edgar Degas', artistSlug: 'edgar-degas', work: 'Danseuse', house: "Sotheby's", hammer: '$15.2M', premium: '+8%', medium: 'paintings', date: '1 week ago' },
    { id: '19c2', artist: 'Pierre-Auguste Renoir', artistSlug: 'pierre-auguste-renoir', work: 'Bal du moulin', house: "Christie's", hammer: '$12.8M', premium: '+5%', medium: 'paintings', date: '2 weeks ago' },
    { id: '19c3', artist: 'Auguste Rodin', artistSlug: 'auguste-rodin', work: 'Le Penseur', house: "Sotheby's", hammer: '$8.4M', premium: '+18%', medium: 'sculpture', date: '3 weeks ago' },
  ],
  'old-masters': [
    { id: 'om1', artist: 'Rembrandt', artistSlug: 'rembrandt', work: 'Portrait of a Man', house: "Christie's", hammer: '$18.2M', premium: '+4%', medium: 'paintings', date: '2 weeks ago' },
    { id: 'om2', artist: 'Canaletto', artistSlug: 'canaletto', work: 'Venice, The Grand Canal', house: "Sotheby's", hammer: '$12.4M', premium: '+8%', medium: 'paintings', date: '1 month ago' },
    { id: 'om3', artist: 'Peter Paul Rubens', artistSlug: 'peter-paul-rubens', work: 'Portrait of a Lady', house: "Christie's", hammer: '$8.6M', premium: '-2%', medium: 'paintings', date: '1 month ago' },
  ],
}

// Movement trends by era
const movementTrendsByEra: Record<string, { id: string; name: string; volume: string; change: number; artists: number }[]> = {
  'contemporary': [
    { id: 'street-art', name: 'Street Art', volume: '$380M', change: 18.6, artists: 245 },
    { id: 'neo-expressionism', name: 'Neo-Expressionism', volume: '$520M', change: 12.4, artists: 128 },
    { id: 'african-contemporary', name: 'African Contemporary', volume: '$85M', change: 42.1, artists: 86 },
    { id: 'figurative-revival', name: 'Figurative Revival', volume: '$120M', change: 28.4, artists: 156 },
    { id: 'digital-art', name: 'Digital Art', volume: '$45M', change: 35.2, artists: 342 },
  ],
  'post-war': [
    { id: 'abstract-expressionism', name: 'Abstract Expressionism', volume: '$890M', change: 6.8, artists: 82 },
    { id: 'pop-art', name: 'Pop Art', volume: '$1.4B', change: 8.5, artists: 124 },
    { id: 'minimalism', name: 'Minimalism', volume: '$420M', change: 2.1, artists: 56 },
    { id: 'color-field', name: 'Color Field', volume: '$245M', change: 5.4, artists: 38 },
    { id: 'conceptual-art', name: 'Conceptual Art', volume: '$180M', change: 4.2, artists: 92 },
  ],
  'modern': [
    { id: 'impressionism', name: 'Impressionism', volume: '$1.2B', change: 4.2, artists: 68 },
    { id: 'cubism', name: 'Cubism', volume: '$780M', change: 1.8, artists: 42 },
    { id: 'surrealism', name: 'Surrealism', volume: '$680M', change: 3.2, artists: 86 },
    { id: 'fauvism', name: 'Fauvism', volume: '$320M', change: 5.8, artists: 24 },
    { id: 'expressionism', name: 'Expressionism', volume: '$450M', change: 2.4, artists: 78 },
  ],
  '19th-century': [
    { id: 'romanticism', name: 'Romanticism', volume: '$380M', change: 2.8, artists: 56 },
    { id: 'realism', name: 'Realism', volume: '$290M', change: 3.4, artists: 82 },
    { id: 'neoclassicism', name: 'Neoclassicism', volume: '$145M', change: 1.2, artists: 34 },
    { id: 'pre-raphaelite', name: 'Pre-Raphaelite', volume: '$120M', change: 4.5, artists: 18 },
  ],
  'old-masters': [
    { id: 'baroque', name: 'Baroque', volume: '$680M', change: -1.8, artists: 124 },
    { id: 'renaissance', name: 'Renaissance', volume: '$520M', change: -2.4, artists: 86 },
    { id: 'rococo', name: 'Rococo', volume: '$180M', change: 1.2, artists: 42 },
  ],
}

// Geographic breakdown
const geographicData = [
  { region: 'Americas', share: 38, change: 8.4, topCity: 'New York' },
  { region: 'Europe', share: 30, change: 5.2, topCity: 'London' },
  { region: 'Asia-Pacific', share: 25, change: 15.8, topCity: 'Hong Kong' },
  { region: 'Middle East', share: 7, change: 22.1, topCity: 'Dubai' },
]

// =============================================================================
// COMPONENTS
// =============================================================================

function MediumIcon({ medium, className = '' }: { medium: MediumId; className?: string }) {
  switch (medium) {
    case 'paintings':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="5" width="18" height="14" rx="1" />
          <path d="M3 15l5-4 3 2 5-4 5 4" />
          <circle cx="16" cy="9" r="1.5" />
        </svg>
      )
    case 'sculpture':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="8" r="4" />
          <ellipse cx="12" cy="19" rx="6" ry="2" />
          <line x1="12" y1="12" x2="12" y2="17" />
        </svg>
      )
    case 'prints':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="6" y="3" width="12" height="15" rx="1" />
          <rect x="8" y="5" width="8" height="9" rx="0.5" />
          <line x1="8" y1="16" x2="16" y2="16" />
          <line x1="10" y1="19" x2="14" y2="19" />
        </svg>
      )
    case 'photography':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="2" y="6" width="20" height="14" rx="2" />
          <circle cx="12" cy="13" r="4" />
          <circle cx="12" cy="13" r="2" />
          <path d="M6 6V5a1 1 0 011-1h2l1 2" />
        </svg>
      )
    case 'drawings':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 18 Q8 8, 12 14 T20 10" strokeLinecap="round" />
          <path d="M6 20 Q10 16, 14 18" strokeLinecap="round" opacity="0.5" />
          <circle cx="18" cy="8" r="2" />
        </svg>
      )
    case 'mixed-media':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="4" y="8" width="10" height="10" rx="1" />
          <circle cx="17" cy="11" r="5" />
          <polygon points="12,4 16,10 8,10" />
        </svg>
      )
    default:
      return null
  }
}

function Sparkline({ data, positive }: { data: number[]; positive: boolean }) {
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const width = 60
  const height = 24

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * height
    return `${x},${y}`
  }).join(' ')

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={positive ? 'var(--positive)' : 'var(--negative)'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function CategoryPage({ params }: { params: Promise<{ category: string }> }) {
  const { category } = use(params)
  const [selectedMedium, setSelectedMedium] = useState<'all' | MediumId>('all')
  const { user } = useAuth()
  const isFreeUser = user?.tier === 'free' || !user

  const periodId = category as PeriodId
  const data = categoryData[category] || categoryData['contemporary']
  const topArtists = topArtistsByEra[category] || topArtistsByEra['contemporary']
  const recentSales = recentSalesByEra[category] || recentSalesByEra['contemporary']
  const movementTrends = movementTrendsByEra[category] || movementTrendsByEra['contemporary']

  // Filtered values based on medium
  const filteredVolume = selectedMedium === 'all' ? data.volumes.all : data.volumes[selectedMedium]
  const filteredLots = selectedMedium === 'all' ? data.lots.all : data.lots[selectedMedium]
  const filteredChange = selectedMedium === 'all' ? data.changes.all : data.changes[selectedMedium]

  const filteredSales = useMemo(() => {
    if (selectedMedium === 'all') return recentSales
    return recentSales.filter(s => s.medium === selectedMedium)
  }, [selectedMedium, recentSales])

  const filteredArtists = useMemo(() => {
    if (selectedMedium === 'all') return topArtists
    return topArtists.filter(a => a.medium === selectedMedium)
  }, [selectedMedium, topArtists])

  // AI Outlook - condensed
  const aiSignals = [
    { signal: filteredChange >= 10 ? 'Strong momentum' : filteredChange >= 0 ? 'Steady growth' : 'Cooling market', type: filteredChange >= 0 ? 'positive' : 'negative' },
    { signal: 'Asian buyers active', type: 'info' },
    { signal: 'Quality premium rising', type: 'positive' },
  ]

  return (
    <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm text-text-tertiary mb-4">
        <Link href="/app/market" className="hover:text-text-secondary transition-colors">Market</Link>
        <span>›</span>
        <span className="text-text-primary">{data.name}</span>
      </div>

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-display text-2xl md:text-3xl font-light">{data.name}</h1>
            <span className="text-sm text-text-tertiary">{data.dateRange}</span>
          </div>
          <p className="text-text-secondary text-sm">{data.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 rounded-full text-sm border border-border text-text-secondary hover:border-border-hover transition-all flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Alert
          </button>
          <TierGate feature="export" showPrompt={false}>
            <button className="px-3 py-1.5 rounded-full text-sm bg-accent-primary text-white flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export
            </button>
          </TierGate>
        </div>
      </div>

      {/* Medium Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setSelectedMedium('all')}
          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
            selectedMedium === 'all' ? 'bg-text-primary text-white' : 'bg-card border border-border text-text-secondary hover:border-border-hover'
          }`}
        >
          All
        </button>
        {MEDIUMS.map((m) => (
          <button
            key={m.id}
            onClick={() => setSelectedMedium(m.id)}
            className={`px-3 py-1.5 rounded-full text-sm transition-all flex items-center gap-1.5 ${
              selectedMedium === m.id ? 'bg-accent-primary text-white' : 'bg-card border border-border text-text-secondary hover:border-border-hover'
            }`}
          >
            <MediumIcon medium={m.id} className="w-3.5 h-3.5" />
            {m.name}
          </button>
        ))}
      </div>

      {/* Stats + AI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Volume</p>
          <p className="text-xl font-display font-light">{filteredVolume}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Lots</p>
          <p className="text-xl font-display font-light">{filteredLots}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">YoY</p>
          <p className="text-xl font-display font-light" style={{ color: filteredChange >= 0 ? 'var(--positive)' : 'var(--negative)' }}>
            {filteredChange >= 0 ? '+' : ''}{filteredChange}%
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Avg. Hammer</p>
          <p className="text-xl font-display font-light">{data.avgHammer}</p>
        </div>
        {/* AI Signals - condensed */}
        <div className="card p-4 border-l-2" style={{ borderLeftColor: 'var(--accent-primary)' }}>
          <p className="text-xs text-text-tertiary mb-1 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            AI Signal
          </p>
          <p className="text-sm font-medium" style={{ color: filteredChange >= 0 ? 'var(--positive)' : 'var(--negative)' }}>
            {aiSignals[0].signal}
          </p>
        </div>
      </div>

      {/* Two Column: Artists + Movements */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Top Artists */}
        <section className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Top Artists</h2>
            <Link href="/app/artists" className="text-xs text-accent-primary hover:underline">View all →</Link>
          </div>
          <div className="space-y-2">
            {filteredArtists.slice(0, 5).map((artist, i) => (
              <Link
                key={artist.slug}
                href={`/app/artists/${artist.slug}`}
                className="flex items-center gap-3 py-2 hover:bg-page -mx-2 px-2 rounded-lg transition-colors"
              >
                <span className="text-xs text-text-tertiary w-4">{i + 1}</span>
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                  style={{ background: artist.color }}
                >
                  {artist.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{artist.name}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      artist.tier === 'Blue-chip' ? 'bg-accent-primary/10 text-accent-primary' : 'bg-accent-warm/10 text-accent-warm'
                    }`}>
                      {artist.tier}
                    </span>
                    <MediumIcon medium={artist.medium} className="w-3 h-3 text-text-tertiary" />
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium tabular-nums">{artist.volume}</p>
                  <p className={`text-xs tabular-nums ${artist.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {artist.change >= 0 ? '+' : ''}{artist.change}%
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Movement Trends */}
        <section className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Movement Trends</h2>
            <span className="text-xs text-text-tertiary">12 months</span>
          </div>
          <div className="space-y-2">
            {movementTrends.slice(0, 5).map((movement) => (
              <Link
                key={movement.id}
                href={`/app/market/movement/${movement.id}`}
                className="flex items-center justify-between py-2 hover:bg-page -mx-2 px-2 rounded-lg transition-colors"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium">{movement.name}</p>
                  <p className="text-xs text-text-tertiary">{movement.artists} artists</p>
                </div>
                <div className="flex items-center gap-4">
                  <Sparkline data={[40, 42, 45, 48, 52, 55, 58, 62, 65].map(v => v * (movement.change / 10 + 1))} positive={movement.change >= 0} />
                  <div className="text-right w-20">
                    <p className="text-sm font-medium tabular-nums">{movement.volume}</p>
                    <p className={`text-xs tabular-nums ${movement.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                      {movement.change >= 0 ? '+' : ''}{movement.change}%
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* Recent Sales + Geographic */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Recent Sales - 2 columns */}
        <section className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Recent Sales</h2>
            <Link href="/app/market" className="text-xs text-accent-primary hover:underline">View all →</Link>
          </div>
          <div className="space-y-2">
            {filteredSales.slice(0, 4).map((sale) => (
              <Link
                key={sale.id}
                href={`/app/artists/${sale.artistSlug}`}
                className="flex items-center gap-3 py-2 hover:bg-page -mx-2 px-2 rounded-lg transition-colors"
              >
                {/* Lot image with medium icon */}
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--page)' }}
                >
                  <MediumIcon medium={sale.medium} className="w-6 h-6 text-text-tertiary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{sale.work}</p>
                  <p className="text-xs text-text-tertiary">{sale.artist} · {sale.house} · {sale.date}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium tabular-nums">{sale.hammer}</p>
                  <p className={`text-xs tabular-nums ${sale.premium.startsWith('+') ? 'text-positive' : 'text-negative'}`}>
                    {sale.premium} vs est.
                  </p>
                </div>
              </Link>
            ))}
            {filteredSales.length === 0 && (
              <p className="text-sm text-text-tertiary text-center py-4">No recent sales for this medium.</p>
            )}
          </div>
        </section>

        {/* Geographic */}
        <section className="card p-5">
          <h2 className="font-medium mb-4">By Region</h2>
          <div className="space-y-3">
            {geographicData.map((geo) => (
              <div key={geo.region}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">{geo.region}</span>
                  <span className={`text-xs tabular-nums ${geo.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {geo.change >= 0 ? '+' : ''}{geo.change}%
                  </span>
                </div>
                <div className="w-full bg-page rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${geo.share}%`, background: 'var(--accent-primary)' }}
                  />
                </div>
                <p className="text-xs text-text-tertiary mt-0.5">{geo.share}% · {geo.topCity}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Upgrade CTA for free users */}
      {isFreeUser && (
        <section className="card p-5 bg-gradient-to-br from-accent-primary/5 to-transparent border-accent-primary/20">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1">
              <h3 className="font-medium mb-1">Access deeper insights</h3>
              <p className="text-sm text-text-secondary">
                30-year history, AI recommendations, and export tools.
              </p>
            </div>
            <Link
              href="/get-started"
              className="px-5 py-2 rounded-xl font-medium text-sm text-white flex-shrink-0"
              style={{ background: 'var(--accent-primary)' }}
            >
              From $199/mo
            </Link>
          </div>
        </section>
      )}
    </div>
  )
}
